/**
 * B2BInquiryModal Component
 * Modern modal-style inquiry form triggered by "Get Quote" button
 *
 * Features:
 * - Clean, centered modal design
 * - Header with title and subheading
 * - Selected product banner
 * - Form fields: Full Name, Phone, Email, Location, Quantity, Additional Requirements
 * - Consent checkbox
 * - Submit button with loading state
 * - Footer note about response time
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  X,
  Send,
  Loader2,
  CheckCircle,
  Package,
  MessageSquare,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { B2BInquiryModalProps, B2BInquiryFormData } from '../types/b2bMarketplace.types';

/**
 * Form Validation Schema
 */
const inquiryFormSchema = z.object({
  fullName: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name cannot exceed 100 characters')
    .trim(),
  phone: z
    .string()
    .length(10, 'Phone number must be exactly 10 digits')
    .regex(/^[6-9]\d{9}$/, 'Please enter a valid 10-digit phone number starting with 6-9'),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Please enter a valid email address')
    .regex(/^[a-z0-9._%+-]+@gmail\.com$/, 'Email must be a @gmail.com address (e.g. sample@gmail.com)'),
  location: z
    .string()
    .min(2, 'Location must be at least 2 characters')
    .max(100, 'Location cannot exceed 100 characters')
    .trim(),
  quantity: z
    .number({
      required_error: 'Quantity is required',
      invalid_type_error: 'Quantity must be a number',
    })
    .positive('Quantity must be greater than 0'),
  additionalRequirements: z.string().max(1000, 'Message cannot exceed 1000 characters').optional(),
  agreeToContact: z.boolean().refine((val) => val === true, {
    message: 'You must agree to receive communications',
  }),
});

type FormData = z.infer<typeof inquiryFormSchema>;

/**
 * B2BInquiryModal Component
 */
export const B2BInquiryModal: React.FC<B2BInquiryModalProps> = ({
  isOpen,
  onClose,
  product,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedInquiryId, setSubmittedInquiryId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(inquiryFormSchema),
    defaultValues: {
      fullName: '',
      phone: '',
      email: '',
      location: '',
      quantity: product?.moq || 1,
      additionalRequirements: '',
      agreeToContact: false,
    },
  });

  const agreeToContact = watch('agreeToContact');

  // Reset form when modal opens/closes or product changes
  useEffect(() => {
    if (isOpen && product) {
      reset({
        fullName: '',
        phone: '',
        email: '',
        location: '',
        quantity: product.moq,
        additionalRequirements: '',
        agreeToContact: false,
      });
      setSubmitSuccess(false);
      setSubmitError(null);
      setSubmittedInquiryId(null);
    }
  }, [isOpen, product, reset]);

  const handleFormSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const inquiryId = await onSubmit(data);
      setSubmittedInquiryId(inquiryId || null);

      setSubmitSuccess(true);

      // Auto-close after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('[B2BInquiryModal] Submit error:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Failed to submit inquiry. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-[540px] p-0 bg-white rounded-2xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 bg-gradient-to-r from-green-50 to-blue-50 border-b">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MessageSquare className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-gray-900">
                Get a Custom Quote
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600 mt-1">
                Fill in your details and we'll prepare a personalized quote for you.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Selected Product Banner */}
        <div className="mx-6 mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded">
            <Package className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-blue-600 font-medium">Product Selected</p>
            <p className="text-sm text-gray-900 font-semibold truncate">
              {product.name} ({product.sku})
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="px-6 py-4 space-y-4">
          {/* Success Message */}
          {submitSuccess && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <AlertDescription className="text-green-900">
                <span className="font-medium">Inquiry submitted successfully!</span>
                {submittedInquiryId && (
                  <>
                    <br />
                    <span className="text-sm">Reference ID: <strong>#{submittedInquiryId}</strong></span>
                  </>
                )}
                <br />
                We'll prepare your quote within 2–4 hours.
              </AlertDescription>
            </Alert>
          )}

          {/* Error Message */}
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {!submitSuccess && (
            <>
              {/* Full Name */}
              <div className="space-y-1.5">
                <Label htmlFor="fullName" className="text-sm font-medium">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="fullName"
                  placeholder="Enter your full name"
                  className={cn(
                    'h-11 rounded-lg',
                    errors.fullName && 'border-red-500 focus-visible:ring-red-500'
                  )}
                  disabled={isSubmitting}
                  {...register('fullName')}
                />
                {errors.fullName && (
                  <p className="text-xs text-red-600">{errors.fullName.message}</p>
                )}
              </div>

              {/* Phone and Email Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="9876543210"
                    className={cn(
                      'h-11 rounded-lg',
                      errors.phone && 'border-red-500 focus-visible:ring-red-500'
                    )}
                    disabled={isSubmitting}
                    {...register('phone', {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                      },
                    })}
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-600">{errors.phone.message}</p>
                  )}
                  <p className="text-xs text-gray-500">Enter 10-digit mobile number (e.g. 9876543210)</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="sample@gmail.com"
                    className={cn(
                      'h-11 rounded-lg',
                      errors.email && 'border-red-500 focus-visible:ring-red-500'
                    )}
                    disabled={isSubmitting}
                    {...register('email')}
                  />
                  <p className="text-xs text-gray-500">Must be a @gmail.com address</p>
                  {errors.email && (
                    <p className="text-xs text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              {/* Location and Quantity Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="location" className="text-sm font-medium">
                    Location / City <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="location"
                    placeholder="Enter your city"
                    className={cn(
                      'h-11 rounded-lg',
                      errors.location && 'border-red-500 focus-visible:ring-red-500'
                    )}
                    disabled={isSubmitting}
                    {...register('location')}
                  />
                  {errors.location && (
                    <p className="text-xs text-red-600">{errors.location.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="quantity" className="text-sm font-medium">
                    Quantity Required ({product.moqUnit}){' '}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    min={1}
                    placeholder={`Minimum ${product.moq} ${product.moqUnit}`}
                    className={cn(
                      'h-11 rounded-lg',
                      errors.quantity && 'border-red-500 focus-visible:ring-red-500'
                    )}
                    disabled={isSubmitting}
                    {...register('quantity', { valueAsNumber: true })}
                  />
                  {errors.quantity && (
                    <p className="text-xs text-red-600">{errors.quantity.message}</p>
                  )}
                </div>
              </div>

              {/* Additional Requirements */}
              <div className="space-y-1.5">
                <Label htmlFor="additionalRequirements" className="text-sm font-medium">
                  Additional Requirements{' '}
                  <span className="text-gray-400 font-normal">(Optional)</span>
                </Label>
                <Textarea
                  id="additionalRequirements"
                  rows={3}
                  placeholder="Tell us about any specific requirements, delivery preferences, or questions..."
                  className={cn(
                    'rounded-lg resize-none',
                    errors.additionalRequirements &&
                      'border-red-500 focus-visible:ring-red-500'
                  )}
                  disabled={isSubmitting}
                  {...register('additionalRequirements')}
                />
                {errors.additionalRequirements && (
                  <p className="text-xs text-red-600">
                    {errors.additionalRequirements.message}
                  </p>
                )}
              </div>

              {/* Consent Checkbox */}
              <div className="flex items-start space-x-3 pt-2">
                <Checkbox
                  id="agreeToContact"
                  checked={agreeToContact}
                  onCheckedChange={(checked) =>
                    setValue('agreeToContact', checked as boolean)
                  }
                  disabled={isSubmitting}
                  className="mt-0.5"
                />
                <Label
                  htmlFor="agreeToContact"
                  className={cn(
                    'text-sm text-gray-600 leading-relaxed cursor-pointer',
                    errors.agreeToContact && 'text-red-600'
                  )}
                >
                  I agree to receive quote and product information via email and WhatsApp
                </Label>
              </div>
              {errors.agreeToContact && (
                <p className="text-xs text-red-600 -mt-2">
                  {errors.agreeToContact.message}
                </p>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className={cn(
                  'w-full h-12 mt-4',
                  'bg-green-600 hover:bg-green-700',
                  'text-white font-semibold text-base',
                  'rounded-lg shadow-md',
                  'transition-all duration-200',
                  'hover:shadow-lg'
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Submit Inquiry
                  </>
                )}
              </Button>
            </>
          )}
        </form>

        {/* Footer Note */}
        <div className="px-6 py-3 bg-gray-50 border-t text-center">
          <p className="text-xs text-gray-500">
            We'll prepare your quote within 2–4 hours.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default B2BInquiryModal;
